/* ============================================================
   MM Newsletter Popup
   - Shows a site-wide signup popup for guests who haven't subscribed.
   - On signup: submits the real Shopify customer form natively into a hidden
     iframe (so Shopify's captcha/spam protection still runs but the page does
     not reload), then optionally applies a discount code and adds a reward
     product to the cart.
   ============================================================ */
(function () {
  const SUBSCRIBED_COOKIE = 'mm_np_subscribed';
  const DISMISSED_COOKIE = 'mm_np_dismissed';
  // Session-scoped dismissal: closing the popup keeps it hidden for the rest of
  // the browsing session (all pages, until the tab/browser session ends),
  // regardless of the "Remember dismissal" setting.
  const DISMISSED_SESSION_KEY = 'mm_np_dismissed_session';
  const CART_SECTIONS = 'mm-cart-drawer,cart-icon-bubble';

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function isDismissedThisSession() {
    try {
      return window.sessionStorage.getItem(DISMISSED_SESSION_KEY) === '1';
    } catch (error) {
      // Storage can be unavailable (private mode / blocked cookies).
      return false;
    }
  }

  function markDismissedThisSession() {
    try {
      window.sessionStorage.setItem(DISMISSED_SESSION_KEY, '1');
    } catch (error) {
      /* no-op */
    }
  }

  function setCookie(name, value, days) {
    let expires = '';
    if (days && days > 0) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  class MmNewsletterPopup extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.shown = false;
    }

    connectedCallback() {
      this.designMode = this.dataset.designMode === 'true' || (window.Shopify && window.Shopify.designMode);
      this.panel = this.querySelector('.mm-np__panel');
      this.form = this.querySelector('.mm-np__form');
      this.submitBtn = this.querySelector('[data-np-submit]');
      this.message = this.querySelector('[data-np-message]');
      this.successEl = this.querySelector('[data-np-success]');
      this.statusEl = this.querySelector('[data-np-status]');
      this.statusTextEl = this.querySelector('[data-np-status-text]');

      this.querySelectorAll('[data-np-close]').forEach((el) =>
        el.addEventListener('click', () => this.hide('dismiss'))
      );
      const overlay = this.querySelector('[data-np-overlay]');
      if (overlay) overlay.addEventListener('click', () => this.hide('dismiss'));

      // Shopify's /contact endpoint is protected by reCAPTCHA / spam protection.
      // A plain fetch() POST has no captcha token, so it is rejected with a 400.
      // Instead we let the real customer form submit *natively* — which lets
      // Shopify's own injected JS attach the captcha token — but we retarget it
      // into a hidden iframe so the visible page never reloads. When the iframe
      // finishes loading, the signup has been processed and we run the reward
      // flow (discount + free product) without leaving the page.
      this.setupIframeSubmit();

      if (this.designMode) {
        // Always render in the theme editor so it can be styled.
        this.removeAttribute('hidden');
        this.classList.add('is-active', 'mm-np--preview');
        return;
      }

      if (getCookie(SUBSCRIBED_COOKIE) || getCookie(DISMISSED_COOKIE) || isDismissedThisSession()) return;

      this.scheduleShow();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      if (this.exitHandler) document.removeEventListener('mouseout', this.exitHandler);
      window.clearTimeout(this._submitTimer);
      if (this.frame && this.frame.parentNode) this.frame.parentNode.removeChild(this.frame);
    }

    scheduleShow() {
      const trigger = this.dataset.trigger || 'delay';
      const delay = parseInt(this.dataset.delay, 10) || 0;
      const isTouch = window.matchMedia('(max-width: 749px)').matches;

      if (trigger === 'immediate') {
        setTimeout(() => this.show(), 400);
        return;
      }

      if (trigger === 'scroll') {
        this.scrollHandler = () => {
          const scrolled = window.scrollY + window.innerHeight;
          const threshold = document.body.scrollHeight * 0.35;
          if (scrolled >= threshold) this.show();
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        return;
      }

      if (trigger === 'exit' && !isTouch) {
        this.exitHandler = (event) => {
          if (event.clientY <= 0) this.show();
        };
        document.addEventListener('mouseout', this.exitHandler);
        // Safety net so mobile / no-exit users still see it.
        setTimeout(() => this.show(), Math.max(delay, 12) * 1000);
        return;
      }

      setTimeout(() => this.show(), delay * 1000);
    }

    show() {
      if (this.shown) return;
      if (getCookie(SUBSCRIBED_COOKIE) || getCookie(DISMISSED_COOKIE) || isDismissedThisSession()) return;
      this.shown = true;

      if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
      if (this.exitHandler) document.removeEventListener('mouseout', this.exitHandler);

      this.removeAttribute('hidden');
      // Force reflow so the transition runs.
      void this.offsetWidth;
      this.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);

      const focusTarget = this.querySelector('.mm-np__input') || this.panel;
      if (focusTarget) {
        window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 350);
      }
    }

    hide(reason) {
      if (this.designMode) return;
      this.classList.remove('is-active');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);

      if (reason === 'dismiss') {
        // Always remember the dismissal for the rest of this browsing session,
        // so the popup does not reappear on the next page the visitor opens.
        markDismissedThisSession();

        // "Remember dismissal" additionally keeps it hidden across sessions for
        // the configured number of days.
        if (this.dataset.rememberDismiss !== 'false') {
          const days = parseInt(this.dataset.frequency, 10);
          setCookie(DISMISSED_COOKIE, '1', isNaN(days) ? 7 : days);
        }
      }

      window.setTimeout(() => this.setAttribute('hidden', ''), 350);
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.hide('dismiss');
    }

    setBusy(busy) {
      if (!this.submitBtn) return;
      this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.submitBtn.disabled = !!busy;
    }

    showMessage(text, isError) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.hidden = !text;
      this.message.classList.toggle('mm-np__message--error', !!isError);
    }

    showStatus(text) {
      if (!this.statusEl) return;
      if (this.statusTextEl) this.statusTextEl.textContent = text || '';
      this.statusEl.hidden = false;
    }

    hideStatus() {
      if (this.statusEl) this.statusEl.hidden = true;
    }

    setupIframeSubmit() {
      if (!this.form) this.form = this.querySelector('.mm-np__form') || this.querySelector('form');
      if (!this.form) return;

      // In the theme editor we never submit for real — just preview success.
      if (this.designMode) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.showSuccess();
        });
        return;
      }

      // A hidden iframe that receives the native form POST so the page that the
      // user is looking at doesn't navigate.
      const frameName = 'mm-np-frame-' + Math.random().toString(36).slice(2);
      const frame = document.createElement('iframe');
      frame.name = frameName;
      frame.title = 'newsletter-signup';
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.style.cssText =
        'position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;left:-9999px;top:-9999px;';
      document.body.appendChild(frame);
      this.frame = frame;

      // Point the real customer form at the hidden iframe.
      this.form.setAttribute('target', frameName);
      this.form.setAttribute('action', '/contact');
      this.form.setAttribute('method', 'post');
      this.form.setAttribute('accept-charset', 'UTF-8');

      frame.addEventListener('load', () => this.onFrameLoad());

      this.form.addEventListener('submit', (e) => {
        // Let invalid forms surface native validation and stop here.
        if (typeof this.form.reportValidity === 'function' && !this.form.reportValidity()) {
          e.preventDefault();
          return;
        }
        if (this.submitting) return; // captcha may re-dispatch submit; ignore extras
        this.submitting = true;
        this.awaitingSubmit = true;
        this.setBusy(true);
        this.showMessage('');
        this.showStatus('Subscribing…');

        // IMPORTANT: do not preventDefault — the native submit must proceed so
        // Shopify's captcha token is attached and the POST lands in the iframe.

        // Fallback so the UI never gets stuck if the captcha is cancelled or the
        // network hangs.
        window.clearTimeout(this._submitTimer);
        this._submitTimer = window.setTimeout(() => {
          if (!this.awaitingSubmit) return;
          this.awaitingSubmit = false;
          this.submitting = false;
          this.hideStatus();
          this.setBusy(false);
          this.showMessage('Something went wrong. Please try again.', true);
        }, 30000);
      });
    }

    onFrameLoad() {
      // The iframe fires "load" once on initial (blank) load — ignore that and
      // only react to the load that follows an actual submission.
      if (!this.awaitingSubmit) return;
      this.awaitingSubmit = false;
      window.clearTimeout(this._submitTimer);
      this.completeSignup();
    }

    async completeSignup() {
      const variantId = this.dataset.rewardVariant;
      const discountCode = this.dataset.discountCode;

      try {
        if (discountCode) {
          this.showStatus('Applying your discount code…');
          try {
            await this.applyDiscount(discountCode);
          } catch (error) {
            console.error('[mm-newsletter-popup] discount error', error);
          }
        }

        if (variantId) {
          this.showStatus('Adding your free product to the cart…');
          try {
            await this.addReward(variantId);
          } catch (error) {
            console.error('[mm-newsletter-popup] add reward error', error);
          }
        }

        setCookie(SUBSCRIBED_COOKIE, '1', 365);
        this.hideStatus();
        this.showSuccess();
        this.scheduleSuccessClose();
      } catch (error) {
        console.error('[mm-newsletter-popup]', error);
        this.hideStatus();
        this.setBusy(false);
        this.submitting = false;
        this.showMessage('Something went wrong. Please try again.', true);
      }
    }

    showSuccess() {
      if (this.successEl) this.successEl.hidden = false;
      this.setBusy(false);
    }

    scheduleSuccessClose() {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = window.setTimeout(() => this.hide('subscribed'), 4500);
    }

    async addReward(variantId) {
      const routes = (window.theme && window.theme.routes) || window.routes || {};
      const addUrl = routes.cart_add_url || '/cart/add';

      // The cart intercept overrides window.fetch for /cart/add: it injects the
      // sections, refreshes the drawer and opens it automatically.
      const response = await fetch(addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { _mm_newsletter_gift: 'true' },
            },
          ],
          sections: CART_SECTIONS,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error('cart/add failed: ' + response.status + ' ' + text);
      }
      return response.json();
    }

    async applyDiscount(code) {
      const routes = (window.theme && window.theme.routes) || window.routes || {};
      const fetchConfig =
        (window.theme && window.theme.utils && window.theme.utils.fetchConfig) || null;

      // Sets the discount cookie so it also carries through to checkout.
      fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'manual',
      }).catch(() => {});

      if (!routes.cart_update_url) return;

      const config = fetchConfig
        ? fetchConfig('json')
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          };

      const response = await fetch(routes.cart_update_url, {
        ...config,
        body: JSON.stringify({ discount: code, sections: CART_SECTIONS }),
      });
      const cart = await response.json();

      // If no reward product is added, publish so the drawer reflects the discount.
      if (!this.dataset.rewardVariant && window.theme && window.theme.pubsub) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'mm-newsletter-popup',
          cart,
        });
      }
      return cart;
    }
  }

  if (!customElements.get('mm-newsletter-popup')) {
    customElements.define('mm-newsletter-popup', MmNewsletterPopup);
  }
})();
